export const WALLET_KIND = Object.freeze({
  MAIN: 'main',
  POOL_SHARE: 'pool_share',
  POOL_CREDIT: 'pool_credit',
  UNKNOWN: 'unknown',
});

// Cross-component wallet-freshness signal (@vueuse/core useEventBus). Emitted
// wherever money is released outside the normal transaction ledger — e.g.
// cancelling an out-of-stock line, whose hold release is a DERIVED wallet
// number, never a transaction row — so the wallet tab (which, per F7's
// routable tabs, may already be mounted and just hidden) re-reads its balance.
export const WALLET_REFRESH_EVENT = 'wallet:refresh';

export const WALLET_REQUEST_TYPE = Object.freeze({
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
});

export const WALLET_REQUEST_STATUS = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
});

// Exhaustive per the frozen BE `kind` enum — see
// ../../backend/_bmad-output/sync/briefs/2026-07-06-transaction-detail-attributes.md.
// `kind` is `meta.type` when present; else `opening_balance` when `meta.method` is
// opening_balance (really emitted since 2026-09-28 — see
// ../../backend/_bmad-output/sync/briefs/2026-09-28-wallet-row-clarity-attributes.md);
// else a sign fallback (deposit/withdrawal).
export const TRANSACTION_KIND = Object.freeze({
  // Legacy/guessed values kept for back-compat — real values are the ones below.
  ORDER: 'order',
  COMMISSION: 'commission',
  POOL_CREDIT: 'pool_credit',
  POOL_SHARE: 'pool_share',
  // Cash / wallet (default wallet)
  DEPOSIT: 'deposit',
  WITHDRAWAL: 'withdrawal',
  TOP_UP: 'top_up',
  OPENING_BALANCE: 'opening_balance',
  SUPPLIER_DIRECT_DEPOSIT: 'supplier_direct_deposit',
  WALLET_REQUEST_REVERSAL: 'wallet_request_reversal',
  WALLET_REQUEST_REAPPLY: 'wallet_request_reapply',
  // Order / invoice
  INVOICE_HOLD: 'invoice_hold',
  INVOICE_SETTLEMENT: 'invoice_settlement',
  // Compensating delta posted when an admin corrects an ALREADY-SETTLED invoice.
  // BIDIRECTIONAL: negative = she was overcharged and money comes back; positive =
  // she was undercharged and money is taken from a box she considers closed. The
  // title resolver gets no sign, so the label must stay direction-neutral — never
  // word it as a refund or as a charge. The signed amount already says which.
  INVOICE_SETTLEMENT_ADJUSTMENT: 'invoice_settlement_adjustment',
  OUT_OF_STOCK_REFUND: 'out_of_stock_refund',
  WRITE_OFF_REFUND: 'write_off_refund',
  GOODWILL_REFUND: 'goodwill_refund',
  // Backend correction (2026-07-29): a goodwill refund wrongly credited into a
  // supplier PoolCredit wallet is withdrawn from there and re-deposited as cash.
  // This NEGATIVE row is that withdrawal — the money MOVED to the member's main
  // wallet, it was not taken from her, and the title must say so. Lives on the
  // pool_credit wallet family.
  GOODWILL_REFUND_REVERSAL: 'goodwill_refund_reversal',
  POOLA_PURCHASE_CASH: 'poola_purchase_cash',
  POOLA_REFUND: 'poola_refund',
  // The club paid her for her own «تدبيسة» piece that sold. Positive, on the main
  // wallet, and never tied to a carton — the buyer's is not hers to see. See
  // ../../backend/_bmad-output/sync/briefs/2026-10-12-tadbeesa-owner-payout.md.
  TADBEESA_PAYOUT: 'tadbeesa_payout',
  // The club paying a wallet shop for what a closed plan bought from it.
  SUPPLIER_COLLECTION_PAYOUT: 'supplier_collection_payout',
  // PoolShare / PoolCredit wallets
  POOLSHARE_EARNED: 'poolshare_earned',
  POOLSHARE_CONSUMED: 'poolshare_consumed',
  // Legacy/guessed pool_credit values kept for back-compat — the real BE `meta.type`
  // tokens are the five below (WalletResource/PoolCreditService, confirmed 2026-07-15).
  POOLCREDIT_GENERATED: 'poolcredit_generated',
  POOLCREDIT_APPLIED: 'poolcredit_applied',
  // Real pool_credit ledger kinds emitted by the backend:
  //   grant          → supplier credit granted (the visible "deposit" on the poolcredit tab)   [pool_credit wallet]
  //   payout         → credit spent out of the poolcredit wallet on delivery                   [pool_credit wallet]
  //   expiry         → unused granted credit expired                                           [pool_credit wallet]
  //   poola_reversal → poola completion credit reversed (offer refunded)                       [pool_credit wallet]
  //   admin_reversal → an admin manually reversed a grant                                      [pool_credit wallet]
  //   earning        → cash earned into the DEFAULT wallet when a hold crystallises            [main wallet, NOT pool_credit]
  POOLCREDIT_GRANT: 'poolcredit_grant',
  POOLCREDIT_PAYOUT: 'poolcredit_payout',
  POOLCREDIT_EARNING: 'poolcredit_earning',
  POOLCREDIT_EXPIRY: 'poolcredit_expiry',
  POOLCREDIT_POOLA_REVERSAL: 'poolcredit_poola_reversal',
  POOLCREDIT_ADMIN_REVERSAL: 'poolcredit_admin_reversal',
});

// The `filter[walletKind]` facets the endpoint accepts. They scope by WALLET FAMILY.
//
// This is a strict SUBSET of WALLET_KIND above, not a copy of it: the wallets
// resource serializes a fourth value, `unknown`, as the default arm of
// WalletResource::kind() for a slug it does not recognise — but the transactions
// filter validates `in:main,pool_share,pool_credit`, so sending `unknown` 400s the
// whole request. Do NOT add UNKNOWN here to "match" WALLET_KIND.
export const WALLET_KIND_FILTER = Object.freeze({
  MAIN: 'main',
  POOL_SHARE: 'pool_share',
  POOL_CREDIT: 'pool_credit',
});

// What the wallet's «بولكريديت» tab filters on to pull EVERY supplier wallet's
// rows in one cursor stream (filter[walletKind][]=pool_credit, no filter[wallet];
// the endpoint's indexQuery already scopes to the member's own wallets). A member
// holds one poolcredit_{supplierId} wallet PER SUPPLIER, so no single wallet id
// can cover this tab.
//
// ⚠️ This REPLACED a `filter[kind]` allow-list of meta.type tokens (grant, payout,
// expiry). That approach was structurally broken: any row written to a PoolCredit
// wallet whose meta.type was off the list was invisible on this tab, and — not
// being on the default wallet — invisible on the main tab too. A confirmed-
// delivered goodwill refund lands on a PoolCredit wallet as `goodwill_refund` and
// so was reachable from NO tab at all. The gap was first logged in FE→BE brief
// 2026-07-15-poolcredit-tab-kind-filter-gap.md and fixed BE-side on 2026-07-29.
//
// Scoping by wallet family is self-healing — a new PoolCredit meta.type shows up
// without an FE release. NEVER go back to enumerating kinds here.
export const POOL_CREDIT_WALLET_KINDS = Object.freeze([WALLET_KIND_FILTER.POOL_CREDIT]);

export const TRANSACTION_STATUS = Object.freeze({
  RESERVED: 'reserved',
  POSTED: 'posted',
});

// The walletType value the PoolShare tab passes down (WalletPage `activeTab`).
export const POOL_SHARE_WALLET_TYPE = 'poolShare';

// The reliable PoolShare type signal from the transactions contract.
// `kind` reads "deposit" for earned mints, so NEVER switch row identity on it.
export const POOL_SHARE_PROGRAM_KIND = Object.freeze({
  EARNING: 'earning',
  GIFT: 'gift',
});

// UI event-type the row resolves to. `generic` is the Phase-1 catch-all for any
// PoolShare row whose program kind is null (bonus & admin-adjustment, which the
// additive contract cannot yet tell apart — see Open Question #1).
export const POOL_SHARE_EVENT_TYPE = Object.freeze({
  EARNING: 'earning',
  GIFT: 'gift',
  GENERIC: 'generic',
});

export const POOL_SHARE_GRANT_PROGRAM = Object.freeze({
  NEW_MEMBER_WELCOME: 'new_member_welcome',
  RETURNING_MEMBER_REACTIVATION: 'returning_member_reactivation',
  ADMIN_CUSTOM: 'admin_custom',
});

export const POOL_SHARE_ACTIVATION_RULE = Object.freeze({
  MIN_ORDERBOX_EGP_CENTS: 'min_orderbox_egp_cents',
  MIN_EARNED_POOLSHARE_CENTS: 'min_earned_poolshare_cents',
});

export const PAYMENT_METHOD = Object.freeze({
  CASH: 'cash',
  INSTAPAY: 'instapay',
  MOBILE_WALLET: 'mobile_wallet',
});

export const ERROR_CODE = Object.freeze({
  // Section 10.1 Idempotency
  IDEMPOTENCY_REQUIRED: 'idempotency.key_required',
  IDEMPOTENCY_CONFLICT: 'idempotency.key_conflict',
  // Section 10.2 Validation
  AMOUNT_TOO_LOW: 'wallet_request.amount_too_low',
  AMOUNT_TOO_HIGH: 'wallet_request.amount_too_high',
  INSUFFICIENT_BALANCE: 'wallet_request.insufficient_balance',
  PAYMENT_ACCOUNT_REQUIRED: 'wallet_request.payment_account_required',
  PAYMENT_ACCOUNT_METHOD_MISMATCH: 'wallet_request.payment_account_method_mismatch',
  PAYMENT_ACCOUNT_INACTIVE: 'wallet_request.payment_account_inactive',
  TYPE_NOT_ALLOWED: 'wallet_request.type_not_allowed_for_member',
  ADMIN_FIELD_NOT_WRITABLE: 'wallet_request.admin_field_not_writable',
  // Section 10.3 Lifecycle
  TYPE_IMMUTABLE: 'wallet_request.type_immutable',
  CANNOT_MODIFY_AFTER_APPROVAL: 'wallet_request.cannot_modify_after_approval',
  CANNOT_MODIFY_AFTER_REJECTION: 'wallet_request.cannot_modify_after_rejection',
  CANNOT_CANCEL_AFTER_APPROVAL: 'wallet_request.cannot_cancel_after_approval',
  // A «تدبيسة» owner payout the club drafted: PATCH and DELETE are both refused (409).
  TADBEESA_PAYOUT_NOT_EDITABLE: 'wallet_request.tadbeesa_payout_not_editable',
  // Section 10.4 Auth
  NO_ACTIVE_MEMBERSHIP: 'auth.no_active_membership',
});
